import { BIG_ARROW_CURSOR, BIG_HAND_CURSOR } from './cursors';

/**
 * Shadow DOM styles. Page CSS cannot reach in here, but inherited properties
 * (font, color, line-height, letter-spacing, cursor) still flow in from the
 * host, so `.root` resets them with `all: initial`.
 *
 * Panel colors are fixed on purpose (ticket §7): dark text on white with a
 * blue-700 accent. The school's color is only on the launcher and the small
 * header badge, each with whichever ink (white or near-black) contrasts more.
 *   #111827 on #fff 17.7:1 · #334155 on #fff 10.4:1 · #475569 on #fff 7.6:1
 *   #1d4ed8 on #fff 6.7:1 · #1d4ed8 on #eff6ff 6.2:1 · #6b7280 on #fff 4.8:1
 * On/off state is carried by the blue border, blue tint and check badge
 * together (each at least 3:1 against white), never by color alone.
 */
export const WIDGET_CSS = `
.root {
  all: initial;
  display: block;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 16px;
  line-height: 1.4;
  color: #111827;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
}
.root *, .root *::before, .root *::after { box-sizing: border-box; }
button, input { font: inherit; }
svg { display: block; flex: none; }

/* ---- launcher ---------------------------------------------------------- */
.launcher {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--ea11y-bg);
  color: var(--ea11y-fg);
  box-shadow: 0 0 0 3px #fff, 0 6px 18px rgba(15, 23, 42, 0.32);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.launcher:hover { transform: scale(1.06); box-shadow: 0 0 0 3px #fff, 0 8px 22px rgba(15, 23, 42, 0.38); }
.launcher:focus { outline: none; }
.launcher:focus-visible { outline: 3px solid #111827; outline-offset: 5px; }
.dot {
  position: absolute;
  top: 1px;
  right: 1px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #15803d;
  box-shadow: 0 0 0 2px #fff;
}
.dot[hidden] { display: none; }

/* ---- panel ------------------------------------------------------------- */
@keyframes ea11y-pop {
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: none; }
}
.panel {
  position: absolute;
  bottom: 68px;
  left: 0;
  width: 460px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - var(--ea11y-sheet-bottom, 88px) - 16px);
  overflow-y: auto;
  overscroll-behavior: contain;
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28), 0 2px 8px rgba(15, 23, 42, 0.08);
  padding: 0;
  animation: ea11y-pop 0.16s ease-out;
  transform-origin: bottom left;
}
:host([data-position="right"]) .panel { left: auto; right: 0; transform-origin: bottom right; }
.panel[hidden] { display: none; }
/* Wide screens: beside the launcher, bottoms lined up, so the panel gets the
   launcher's height too and the whole grid fits a 1366x768 laptop. */
@media (min-width: 641px) {
  .panel {
    bottom: 0;
    left: calc(100% + 12px);
    /* Its left edge is where the player starts: past the launcher (and the chatbot's). */
    max-width: calc(100vw - var(--ea11y-reader-inset, 88px) - 16px);
    max-height: calc(100vh - var(--ea11y-host-bottom, 20px) - 16px);
  }
  :host([data-position="right"]) .panel { left: auto; right: calc(100% + 12px); }
}

.header {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 6px 16px;
  background: #fff;
}
.badge {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--ea11y-bg);
  color: var(--ea11y-fg);
}
h2 { flex: 1; margin: 0; font-size: 18px; font-weight: 700; line-height: 1.3; }
h2:focus { outline: none; }
h2:focus-visible { outline: 3px solid #1d4ed8; outline-offset: 2px; border-radius: 4px; }
h2.quiet:focus-visible { outline: none; }
.close, .modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 40px;
  height: 40px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #374151;
  cursor: pointer;
}
.close:hover, .modal-close:hover { background: #f3f4f6; }
.close:focus-visible, .modal-close:focus-visible { outline: 3px solid #1d4ed8; outline-offset: 0; }
.intro { margin: 0; padding: 0 18px 12px; font-size: 13.5px; line-height: 1.4; color: #475569; background: #fff; }

/* ---- tiles: one grid, three a row -------------------------------------- */
.body { position: relative; padding: 12px 14px; background: #f1f5f9; border-top: 1px solid #e2e8f0; }
.tiles { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 90px;
  margin: 0;
  padding: 11px 4px 9px;
  border: 2px solid transparent;
  border-radius: 14px;
  background: #fff;
  color: #1f2937;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.1), 0 1px 1px rgba(15, 23, 42, 0.04);
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}
.tile:hover { border-color: #94a3b8; }
.tile:focus { outline: none; }
.tile:focus-visible { outline: 3px solid #1d4ed8; outline-offset: 2px; }
.tile-icon { display: flex; align-items: center; justify-content: center; height: 28px; color: #334155; }
.tile-icon svg { width: 28px; height: 28px; }
.glyph-text { font-size: 24px; font-weight: 400; line-height: 1; }
/* (i): the tile has a line of help, shown on hover and keyboard focus. */
.tile-info {
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #334155;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 12px;
  font-style: italic;
  font-weight: 700;
  line-height: 1;
}
.tip {
  position: absolute;
  z-index: 2;
  width: max-content;
  max-width: 230px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #111827;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.3);
}
.tip[hidden] { display: none; }
.tile-label { display: block; }
/* Steps: one bar each, filled up to the step in use. */
.steps { display: flex; gap: 4px; width: 74%; margin-top: 2px; }
.steps > span { flex: 1; height: 4px; border-radius: 2px; background: #cbd5e1; }
.steps > span.filled { background: #1d4ed8; }
.tile-check {
  position: absolute;
  top: 8px;
  right: 8px;
  display: none;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #1d4ed8;
  color: #fff;
}
/* On: blue border, a soft blue ring, blue label and icon, and the check. */
.tile.on { border-color: #1d4ed8; box-shadow: 0 0 0 4px #dbeafe; color: #1d4ed8; }
.tile.on .tile-icon { color: #1d4ed8; }
.tile.on .tile-check { display: flex; }

/* ---- read aloud player -------------------------------------------------- */
/* Beside the launcher, centred on it (offsets from layout()), and painted
   before the panel so the panel covers it where the two meet. */
.reader {
  position: fixed;
  bottom: var(--ea11y-reader-bottom, 24px);
  left: var(--ea11y-reader-inset, 88px);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  max-width: calc(100vw - var(--ea11y-reader-inset, 88px) - 16px);
  min-height: 48px;
  padding: 4px 4px 4px 12px;
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.25), 0 1px 4px rgba(15, 23, 42, 0.08);
}
:host([data-position="right"]) .reader { left: auto; right: var(--ea11y-reader-inset, 88px); }
.reader[hidden] { display: none; }
.reader-icon { display: flex; color: #1d4ed8; margin-right: 2px; }
.reader-play, .reader-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  margin: 0;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}
/* Wide enough for its longest label, so Stop and Speed never move under the pointer. */
.reader-play { min-width: 152px; padding: 0 16px 0 12px; border: 0; background: #1d4ed8; color: #fff; }
.reader-play:hover { background: #1e40af; }
.reader-btn { min-width: 40px; padding: 0 10px; border: 1.5px solid #6b7280; background: #fff; color: #111827; }
.reader-btn:hover { background: #f3f4f6; }
.reader-stop, .reader-close { padding: 0; }
.reader-close { border-color: transparent; color: #374151; }
.reader-btn:disabled { border-color: #d1d5db; color: #9ca3af; background: #fff; cursor: default; }
.reader-play:focus-visible, .reader-btn:focus-visible { outline: 3px solid #1d4ed8; outline-offset: 2px; }
.reader-msg { flex-basis: 100%; margin: 0 8px 4px 0; font-size: 13px; line-height: 1.35; color: #334155; }
.reader-msg:empty { display: none; }
.reader-hint { flex-basis: 100%; margin: 0 8px 4px 0; font-size: 13px; line-height: 1.35; color: #475569; }
.reader-hint[hidden] { display: none; }

/* Invert colors is a filter on the whole page, widget included: turn the
   widget's own parts back. Each part is its own positioned box, so the filter
   never becomes the containing block of something fixed inside it. */
.root.inverted { --ea11y-w-invert: invert(1) hue-rotate(180deg); }
/* saturate(a) then saturate(b) is saturate(a * b), so 2 undoes 0.5 and back.
   Desaturate (grayscale) loses the color for good and is left alone. */
.root.sat-low { --ea11y-w-sat: saturate(2); }
.root.sat-high { --ea11y-w-sat: saturate(0.5); }
.root.inverted .launcher,
.root.inverted .panel,
.root.inverted .reader,
.root.inverted .layer,
.root.inverted .guide,
.root.sat-low .launcher,
.root.sat-low .panel,
.root.sat-low .reader,
.root.sat-low .layer,
.root.sat-low .guide,
.root.sat-high .launcher,
.root.sat-high .panel,
.root.sat-high .reader,
.root.sat-high .layer,
.root.sat-high .guide {
  filter: var(--ea11y-w-sat, saturate(1)) var(--ea11y-w-invert, invert(0));
}

/* ---- footer ------------------------------------------------------------ */
/* Sticky, so Reset stays in reach while the settings scroll. */
.footer {
  position: sticky;
  bottom: 0;
  z-index: 1;
  padding: 8px 14px 6px;
  border-top: 1px solid #e5e7eb;
  background: #fff;
}
.actions { display: flex; flex-wrap: wrap; align-items: stretch; gap: 8px; }
.reset, .statement {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  margin: 0;
  padding: 0 14px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
}
/* Equal halves; on a phone each takes its own row. */
.reset { flex: 1 1 190px; border: 0; background: #1f2937; color: #fff; white-space: nowrap; }
.reset:hover { background: #111827; }
.statement { flex: 1 1 190px; border: 1.5px solid #cbd5e1; background: #fff; color: #1d4ed8; white-space: nowrap; }
.statement:hover { background: #eff6ff; border-color: #1d4ed8; }
.statement[hidden] { display: none; }
.reset:focus-visible, .statement:focus-visible { outline: 3px solid #1d4ed8; outline-offset: 2px; }
/* Baseline, not center: the logo's box includes its tall "d" and "l", so
   centering drops the wordmark about 4px below the words around it. An img's
   flex baseline is its bottom edge, which is where the wordmark sits. */
.brand {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 5px;
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 18px;
  color: #6b7280;
}
/* Block, so the link keeps the img's baseline (its bottom edge) in the row. */
.brand-link { display: block; border-radius: 2px; }
.brand-link:focus-visible { outline: 3px solid #1d4ed8; outline-offset: 2px; }
.logo { display: block; width: 54px; height: 18px; }

/* ---- statement dialog -------------------------------------------------- */
.layer {
  position: fixed;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.55);
}
.layer[hidden] { display: none; }
.modal {
  width: 100%;
  max-width: 640px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  overscroll-behavior: contain;
  background: #fff;
  color: #111827;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.35);
  animation: ea11y-pop 0.16s ease-out;
}
.modal-head {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 12px 12px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}
.modal-head h2 { font-size: 20px; }
.modal-body { padding: 12px 24px 24px; font-size: 16px; line-height: 1.6; }
.modal-body h3 { margin: 20px 0 6px; font-size: 17px; font-weight: 700; line-height: 1.35; color: #111827; }
.modal-body p { margin: 0 0 12px; }
.modal-body ul { margin: 0 0 12px; padding-left: 22px; }
.modal-body li { margin: 0 0 4px; }
.modal-body a { color: #1d4ed8; text-decoration: underline; text-underline-offset: 2px; word-break: break-word; }
.modal-body a:focus-visible { outline: 3px solid #1d4ed8; outline-offset: 2px; border-radius: 2px; }

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.guide {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 14px;
  margin-top: -7px;
  background: rgba(17, 24, 39, 0.45);
  border-top: 3px solid #facc15;
  border-bottom: 3px solid #facc15;
  pointer-events: none;
  z-index: 3;
}
.guide[hidden] { display: none; }

.root.big-cursor, .root.big-cursor * { cursor: ${BIG_ARROW_CURSOR} !important; }
.root.big-cursor button, .root.big-cursor button *, .root.big-cursor a, .root.big-cursor label, .root.big-cursor input {
  cursor: ${BIG_HAND_CURSOR} !important;
}

/* Narrow: the player gets its own row above the launcher. */
@media (max-width: 600px) {
  .reader, :host([data-position="right"]) .reader { left: 8px; right: 8px; bottom: var(--ea11y-sheet-bottom, 88px); max-width: none; }
  .reader-icon, .rate-word { display: none; }
}
@media (max-width: 420px) {
  .tiles { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 360px) {
  .reader-play { min-width: 0; }
}
/* Phone width: the panel becomes a sheet above the launcher. */
@media (max-width: 480px) {
  .panel { position: fixed; left: 8px; right: 8px; bottom: var(--ea11y-sheet-bottom, 88px); width: auto; max-width: none; }
  :host([data-position="right"]) .panel { left: 8px; right: 8px; }
  .body { padding: 12px; }
  .tiles { gap: 8px; }
  .modal-body { padding: 8px 18px 20px; }
  .modal-head { padding-left: 18px; }
}
@media (prefers-reduced-motion: reduce) {
  .launcher, .panel, .modal, .tile { transition: none; animation: none; }
  .launcher:hover { transform: none; }
}
/* The reading guide follows a mouse and the big cursor is a mouse cursor, so
   neither does anything on a touch-only device. */
@media (hover: none) and (pointer: coarse) {
  .switch[data-pref="guide"], .switch[data-pref="cursor"] { display: none; }
}
@media (forced-colors: active) {
  .launcher, .badge, .reset, .statement, .tile, .reader, .reader-play, .reader-btn { border: 2px solid ButtonText; }
  .tile.on { border-width: 4px; }
  .tile-check { background: Highlight; color: HighlightText; }
  .steps > span { background: GrayText; }
  .steps > span.filled { background: Highlight; }
  .dot { background: Highlight; }
}
@media print {
  .root { display: none !important; }
}
`;
